export type WeekdayKey =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export interface DaySchedule {
  day: WeekdayKey;
  label: string;
  enabled: boolean;
  start: string;
  end: string;
}

export const DEFAULT_WA_REMINDER_TEMPLATE = `Olá {nome}!

Gostaríamos de confirmar sua consulta:

*{servico}*
{data} às {hora}
{clinica}

Confirma?

Agradecemos!`;

export interface ClinicHoursSettings {
  clinicName: string;
  timezone: string;
  address: string;
  website: string;
  phone: string;
  contactEmail: string;
  waReminderTemplate: string;
  inviteEmailTemplate: string;
  lowPackageThreshold: number;
  lowMensalidadeThreshold: number;
  descontoPromocionalMensalidade: number;
  schedule: DaySchedule[];
}

export const DEFAULT_CLINIC_HOURS: ClinicHoursSettings = {
  clinicName: "Serafisio",
  timezone: "America/Sao_Paulo",
  address: "Rua Coronel Gomes Machado, 118 - Sala 603 - Centro - Niterói - RJ",
  website: "https://www.nitfisio.com.br",
  phone: "",
  contactEmail: "",
  waReminderTemplate: DEFAULT_WA_REMINDER_TEMPLATE,
  inviteEmailTemplate: "",
  lowPackageThreshold: 2,
  lowMensalidadeThreshold: 2,
  descontoPromocionalMensalidade: 0,
  schedule: [
    { day: "monday", label: "Segunda-feira", enabled: true, start: "08:00", end: "18:00" },
    { day: "tuesday", label: "Terça-feira", enabled: true, start: "08:00", end: "18:00" },
    { day: "wednesday", label: "Quarta-feira", enabled: true, start: "08:00", end: "18:00" },
    { day: "thursday", label: "Quinta-feira", enabled: true, start: "08:00", end: "18:00" },
    { day: "friday", label: "Sexta-feira", enabled: true, start: "08:00", end: "18:00" },
    { day: "saturday", label: "Sábado", enabled: true, start: "08:00", end: "12:00" },
    { day: "sunday", label: "Domingo", enabled: false, start: "00:00", end: "00:00" },
  ],
};

const isTime = (value: string) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
export const clampAlertThreshold = (value: number, fallback: number) =>
  Number.isFinite(value) ? Math.min(50, Math.max(1, value)) : fallback;

export const sanitizeClinicHours = (value: unknown): ClinicHoursSettings => {
  if (!value || typeof value !== "object") {
    return DEFAULT_CLINIC_HOURS;
  }

  const parsed = value as Partial<ClinicHoursSettings>;
  const baseByDay = new Map(DEFAULT_CLINIC_HOURS.schedule.map((item) => [item.day, item]));

  const schedule = Array.isArray(parsed.schedule)
    ? parsed.schedule
      .map((item) => {
        if (!item || typeof item !== "object" || !("day" in item)) return null;
        const day = (item as Partial<DaySchedule>).day;
        if (!day || !baseByDay.has(day)) return null;

        const fallback = baseByDay.get(day)!;
        const start = (item as Partial<DaySchedule>).start;
        const end = (item as Partial<DaySchedule>).end;

        return {
          day,
          label: (item as Partial<DaySchedule>).label?.trim() || fallback.label,
          enabled: Boolean((item as Partial<DaySchedule>).enabled),
          start: typeof start === "string" && isTime(start) ? start : fallback.start,
          end: typeof end === "string" && isTime(end) ? end : fallback.end,
        };
      })
      .filter((item): item is DaySchedule => item !== null)
    : [];

  const completeSchedule = DEFAULT_CLINIC_HOURS.schedule.map((defaultDay) =>
    schedule.find((day) => day.day === defaultDay.day) ?? defaultDay,
  );

  return {
    clinicName: parsed.clinicName?.trim() || DEFAULT_CLINIC_HOURS.clinicName,
    timezone: parsed.timezone?.trim() || DEFAULT_CLINIC_HOURS.timezone,
    address: typeof parsed.address === "string" ? parsed.address.trim() : DEFAULT_CLINIC_HOURS.address,
    website: typeof parsed.website === "string" ? parsed.website.trim() : DEFAULT_CLINIC_HOURS.website,
    phone: typeof parsed.phone === "string" ? parsed.phone.trim() : DEFAULT_CLINIC_HOURS.phone,
    contactEmail: typeof parsed.contactEmail === "string" ? parsed.contactEmail.trim() : DEFAULT_CLINIC_HOURS.contactEmail,
    waReminderTemplate: typeof parsed.waReminderTemplate === "string" && parsed.waReminderTemplate.trim() ? parsed.waReminderTemplate : DEFAULT_CLINIC_HOURS.waReminderTemplate,
    inviteEmailTemplate: typeof parsed.inviteEmailTemplate === "string" ? parsed.inviteEmailTemplate : DEFAULT_CLINIC_HOURS.inviteEmailTemplate,
    lowPackageThreshold:
      typeof parsed.lowPackageThreshold === "number"
        ? clampAlertThreshold(parsed.lowPackageThreshold, DEFAULT_CLINIC_HOURS.lowPackageThreshold)
        : DEFAULT_CLINIC_HOURS.lowPackageThreshold,
    lowMensalidadeThreshold:
      typeof parsed.lowMensalidadeThreshold === "number"
        ? clampAlertThreshold(parsed.lowMensalidadeThreshold, DEFAULT_CLINIC_HOURS.lowMensalidadeThreshold)
        : DEFAULT_CLINIC_HOURS.lowMensalidadeThreshold,
    descontoPromocionalMensalidade:
      typeof parsed.descontoPromocionalMensalidade === "number"
        ? Math.min(100, Math.max(0, parsed.descontoPromocionalMensalidade))
        : DEFAULT_CLINIC_HOURS.descontoPromocionalMensalidade,
    schedule: completeSchedule,
  };
};
